import { AuthService, UtilityService } from '.';
import { ApiService } from './api.service';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { catchError, map, Observable, of, switchMap, tap, throwError, forkJoin } from 'rxjs';
import {
    ExamSubscriptionRequestModel,
    InstallmentOptionEnum,
    PackageDataModel,
    UserPackageResponseModel,
    PackageLinkModel,
    PackageRecommendationModel,
    PackageRecommendationRequestModel,
    PackageSubscriptionRequestModel,
    PaymentMethodEnum,
    SenorityOption,
} from '../models/package.model';
import { BypassAPIGlobalHandleEnums, PackageType, PaymentIntentStatus } from '../constants';
import { StripePaymentIntent } from '../models/payment-intent.model';
import { PaymentInfoForForRedirect } from '../models/payment-info-for-redirect.model';
import { BookingDataModel } from '../models/booking.model';
import { RecurrentBookingDataModel } from '../models';
import { AppointmentUserInformation } from '../models/appointment.model';
import { SENIORITY_LEVELS, SeniorityLevelString } from '../constants/packages';
import { CreditMovementHistoryParams, CreditMovementHistoryResponseModel } from '../models/invoice.model';
import { FlowRouteService, FlowStorageKeys } from './flow-route.service';

// Default middle-package durations (in months). 9 months won the 7 vs 9 A/B test
// for both NL and DE on gross margin per registration. See SCRUM-444.
const DEFAULT_DURATION_OPTIONS_MONTHS: Array<string> = ['3', '9', '14'];

@Injectable({
    providedIn: 'root'
})
export class PackagesFlowService {

    hasPackages: boolean = false;
    hasPaidPackage: boolean = false;
    hasActivePackage: boolean = false;
    activePackage: UserPackageResponseModel | null = null;
    totalCredits: string;

    //package = pending & payment intend = pending
    pendingPackage: PackageDataModel;

    enrollingPackageButCreditEnded: PackageDataModel | undefined;

    suspendedPackage: PackageDataModel | undefined;

    isOneInstallment: WritableSignal<boolean> = signal(false);
    currentTab: WritableSignal<number> = signal(0);
    isWarningVisible: WritableSignal<boolean> = signal(false);
    isOnPackagePage: WritableSignal<boolean> = signal(false);

    showExamPackages: WritableSignal<boolean> = signal(false);
    showEntryPackages: WritableSignal<boolean> = signal(false);

    selectedFrequency: WritableSignal<string | null> = signal(null);
    selectedTutorLevel: WritableSignal<string | null> = signal(null);

    entryPackage: PackageRecommendationModel | null = null
    availableDurationsMonths: Array<string> = [...DEFAULT_DURATION_OPTIONS_MONTHS];
    packageCards: Array<PackageRecommendationModel> = [];
    selectedPackage: WritableSignal<PackageRecommendationModel | null> = signal(null);
    userRelations: Array<AppointmentUserInformation> = [];

    bookingDetailsAfterPayment?: BookingDataModel;
    bookingRecurrentDetailsAfterPayment?: RecurrentBookingDataModel;
    tutorForenameAfterPayment?: string;

    skipFirstStep: boolean = false;
    skipSecondStep: boolean = false;
    skipThirdStep: boolean = false;

    hasDismissedPackagesCover: WritableSignal<boolean> = signal(false);
    isAfterBooking: WritableSignal<boolean> = signal(false);

    isDeeplinkFlow: WritableSignal<boolean> = signal(false);
    deeplinkConfig: PackageLinkModel | null = null;
    deeplinkLandingStep: number | null = null;

    private hasDismissedPackagesCoverBeforeDeeplink: boolean = false;
    private isOneInstallmentBeforeDeeplink: boolean = false;
    multipleInstallmentValue: number = 3;
    tutorLevels: Array<SenorityOption> = SENIORITY_LEVELS;
    availableTutorLevels: Array<SenorityOption> = SENIORITY_LEVELS;

    constructor(
        private apiService: ApiService,
        private authService: AuthService,
        private utilityService: UtilityService,
        private flowRouteService: FlowRouteService,
    ) {
        if (this.authService.userDetails?.studentDetails) {
            this.init()
        }
        this.authService.onCurrentUserChange.subscribe((user) => {
            if (user?.studentDetails) {
                this.init()
            }
        })
        this.addSupremePlusIfNeeded();
    }

    init() {
        if (this.isDeeplinkFlow()) {
            return;
        }
        this.resetSelectionState();
        this.checkIfExamOrEntryPackageAvailable()
    }

    checkIfExamOrEntryPackageAvailable() {
        // Uncomment this when we are usinng examPackages again
        // if (this.utilityService.isNL) {
        //     if (!this.isOnPackagePage()) {
        //         const isExamByDuration = this.authService.userDetails?.studentDetails?.expectedTutoringDuration === '0';
        //         const isExamByEducation = this.hasExamYearSearchInterest();
        //         if (isExamByDuration || isExamByEducation) {
        //             this.showExamPackages.set(true)
        //         }
        //         else {
        //             this.showExamPackages.set(false)
        //         }
        //     }
        // }
        if (this.utilityService.isDE) {
            this.checkToAddEntryPackage()
        }
    }

    // private hasExamYearSearchInterest(): boolean {
    //     const searchInterest = this.authService.userDetails?.studentDetails?.searchInterest;
    //     if (!searchInterest?.schoolLevelId || !searchInterest?.schoolYearId) return false;

    //     const EXAM_YEAR_COMBINATIONS = [
    //         { levelId: 5, yearId: 14 }, // Vmbo 4
    //         { levelId: 4, yearId: 18 }, // Havo 5
    //         { levelId: 3, yearId: 19 }, // Vwo 6
    //     ];

    //     return EXAM_YEAR_COMBINATIONS.some(exam =>
    //         exam.levelId == searchInterest.schoolLevelId && exam.yearId == searchInterest.schoolYearId
    //     );
    // }

    checkToAddEntryPackage() {
        this.getUserPackages(+this.authService.userDetails?.id!).subscribe((userPackages) => {
            if (userPackages.packages.length > 0) {
                const hasPackageWithStartDate = userPackages.packages.some(userPackage =>
                    userPackage.userPackage.startDate
                );
                if (!hasPackageWithStartDate) {
                    this.addEntryPackage();
                }
            } else {
                this.addEntryPackage();
            }
        });
    }

    addEntryPackage() {
        if (!this.isOnPackagePage() && !this.isDeeplinkFlow()) {
            this.showEntryPackages.set(true)
            this.getExamPackages().subscribe(() => {
                this.availableDurationsMonths = DEFAULT_DURATION_OPTIONS_MONTHS.slice(0, 2);
            });
        }
    }

    buySelectedPackage() {
        if (this.selectedPackage()?.examPackage) {
            const subscribeData: any = {
                tutorCategory: this.selectedTutorLevel()?.toString(),
                installmentOption: this.isOneInstallment() ? InstallmentOptionEnum.single : InstallmentOptionEnum.multi,
                paymentOption: this.isOneInstallment() ? PaymentMethodEnum.single : PaymentMethodEnum.multi,
                totalInstallment: this.isOneInstallment() ? 1 : 3,
            };
            this.subscribeToExamPackage(subscribeData).subscribe({
                next: (response) => {
                    this.handleResponse(response.paymentIntent, response.userPackage);
                }
            })
        } else {
            const subscribeData: PackageSubscriptionRequestModel = {
                packageId: this.selectedPackage()?.packageId || '',
                improvementPurpose: "",
                durationMonths: this.selectedPackage()?.durationMonths || '',
                lessonsPerMonth: this.selectedPackage()?.lessonsPerMonth?.toString() || '',
                tutorCategory: this.selectedTutorLevel()?.toString() || '',
                installmentOption: this.isOneInstallment() ? InstallmentOptionEnum.single : InstallmentOptionEnum.multi,
                paymentOption: this.isOneInstallment() ? PaymentMethodEnum.single : PaymentMethodEnum.multi,
                totalInstallment: this.isOneInstallment() ? 1 : this.selectedPackage()?.totalInstallment,
            }
            this.subscribeToPackage(subscribeData).subscribe({
                next: (response) => {
                    this.handleResponse(response.paymentIntent, response.userPackage);
                }
            })
        }
    }

    getPackagesRecommendation() {
        const durationMonths = this.availableDurationsMonths;
        const entryPackage = this.showEntryPackages() ? this.entryPackage : null;
        if (this.utilityService.isDE && !this.isDeeplinkFlow()) {
            this.checkToAddEntryPackage()
        }
        const requests = durationMonths.map((durationMonths) => {
            const packageRequest: PackageRecommendationRequestModel = {
                durationMonths: durationMonths,
                lessonsPerMonth: this.selectedFrequency()!,
                tutorCategory: this.selectedTutorLevel()!,
                country: this.utilityService.country,
                totalInstallment: this.isOneInstallment() ? 1 : this.multipleInstallmentValue,
            };
            return this.apiService.post('api/booking/packages/package-recommendation', packageRequest);
        });
        return forkJoin(requests).pipe(
            tap((results: PackageRecommendationModel[]) => {
                this.packageCards = entryPackage ? [entryPackage, ...results] : results;
            })
        );
    }

    initDeeplinkFlow(token: string): Observable<void> {
        this.hasDismissedPackagesCoverBeforeDeeplink = this.hasDismissedPackagesCover();
        this.isOneInstallmentBeforeDeeplink = this.isOneInstallment();
        this.isDeeplinkFlow.set(true);
        return this.resolvePackageLink(token).pipe(
            switchMap((config: PackageLinkModel) => {
                if (config.country !== this.utilityService.country) {
                    return throwError(() => new Error('package_link_country_mismatch'));
                }
                this.applyDeeplinkConfig(config);
                return this.getPackagesRecommendation();
            }),
            map(() => {
                const targetDuration = this.deeplinkConfig?.durationMonths;
                const targetCard = targetDuration != null
                    ? this.packageCards.find(card => String(card.durationMonths) === String(targetDuration))
                    : undefined;
                if (targetCard) {
                    this.selectedPackage.set(targetCard);
                    this.currentTab.set(3);
                    this.deeplinkLandingStep = 4;
                } else {
                    this.currentTab.set(2);
                    this.deeplinkLandingStep = 3;
                }
            }),
            catchError((error) => {
                this.resetDeeplinkState();
                return throwError(() => error);
            })
        );
    }

    resetDeeplinkState() {
        const wasDeeplinkFlow = this.isDeeplinkFlow();
        this.isDeeplinkFlow.set(false);
        this.deeplinkConfig = null;
        this.deeplinkLandingStep = null;
        if (!wasDeeplinkFlow) {
            return;
        }
        this.hasDismissedPackagesCover.set(this.hasDismissedPackagesCoverBeforeDeeplink);
        this.isOneInstallment.set(this.isOneInstallmentBeforeDeeplink);
        if (this.authService.userDetails?.studentDetails) {
            this.init();
        } else {
            this.resetSelectionState();
        }
    }

    getCurrentPackageLink(): Observable<PackageLinkModel | null> {
        const headers = this.utilityService.addHeaders(BypassAPIGlobalHandleEnums.All);
        return this.apiService.getWithHeaders('api/booking/packages/package-links/current', headers).pipe(
            map((response: { link?: PackageLinkModel }) => response?.link ?? null),
            catchError(() => of(null))
        );
    }

    getExamPackages(): Observable<{ offers: Array<PackageRecommendationModel> }> {
        const request = this.apiService.post('api/booking/packages/exam-package-recommendation', {
            tutorCategory: this.selectedTutorLevel(),
            totalInstallment: this.isOneInstallment() ? 1 : 3
        })
        request.subscribe((response) => {
            if (this.showEntryPackages()) {
                this.entryPackage = response.offers.filter(x => x.examPackage)[0];
            } else if (!this.isDeeplinkFlow()) {
                this.packageCards = response.offers;
            }
        })
        return request;
    }

    getUserPackages(userId: number): Observable<{ packages: Array<PackageDataModel> }> {
        return this.apiService.get(`api/booking/packages/user-packages/current?userId=${userId}&statuses=[1,2,3,4]&cacheBuster=${Date.now()}`)
            .pipe(
                map(response => {
                    response.packages = response.packages.map(p => {
                        const _package = p.userPackage;
                        if (!_package.balance) {
                            _package.balance = "0";
                        }
                        return p;
                    });
                    return response;
                }),
                tap(response => {
                    this.hasPackages = response.packages.length > 0;
                    this.hasPaidPackage = response.packages.some(p => p.userPackage.endDate);
                    const pendingPackages = response.packages.filter(x =>
                        (x.userPackage.status == PackageType.Active || x.userPackage.status == PackageType.Pending) &&
                        (x.paymentIntent?.status == PaymentIntentStatus.Pending || x.paymentIntent?.status == PaymentIntentStatus.Canceled));
                    if (pendingPackages.length) {
                        if (pendingPackages.length > 1) {
                            this.pendingPackage = pendingPackages.find(x => x.paymentIntent.currentInstallment != '1') || pendingPackages[0];
                        } else {
                            this.pendingPackage = pendingPackages[0];
                        }
                    }

                    this.enrollingPackageButCreditEnded = response.packages.find(x =>
                        (x.userPackage.status == PackageType.Ended || x.userPackage.status == PackageType.Expired) &&
                        (x.paymentIntent?.status == PaymentIntentStatus.Pending || x.paymentIntent?.status == PaymentIntentStatus.Canceled) &&
                        //  Not first installment for an expired package, we are making sure that the first installment is was payed
                        x.paymentIntent.currentInstallment != '1');

                    this.suspendedPackage = response.packages.find(x => x.userPackage.status == PackageType.Suspended);
                    let activePackages = response.packages.filter(x => x.userPackage.status == PackageType.Active);
                    activePackages = activePackages.sort((a, b) => +a.userPackage.status - +b.userPackage.status);
                    this.totalCredits = activePackages.length ? (activePackages.map(x => +x.userPackage.balance).reduce((a, b) => a + b)).toString() : '';
                    this.hasActivePackage = activePackages.length > 0;
                    this.activePackage = activePackages.length ? activePackages[0].userPackage : null;
                }),
            );
    }

    subscribeToPackage(data: PackageSubscriptionRequestModel): Observable<PackageDataModel> {
        return this.apiService.post(`api/booking/packages/user-packages/subscribe`, data)
    }

    requestCancellation(userPackageId: string): Observable<any> {
        return this.apiService.post(`api/booking/packages/user-packages/request-cancellation/${userPackageId}`, {});
    }

    subscribeToExamPackage(data: ExamSubscriptionRequestModel): Observable<PackageDataModel> {
        return this.apiService.post(`api/booking/packages/user-packages/subscribe-exam-package`, data)
    }

    initiatePaymentIntent(userPackageId: string): Observable<StripePaymentIntent> {
        return this.apiService.post(`api/booking/payment/intent/${userPackageId}`)
    }

    clearUserPackages() {
        this.totalCredits = '';
        this.hasPaidPackage = false;
    }

    saveInfoInLocalStorageForRedirect(paymentInfoForRedirect: PaymentInfoForForRedirect) {
        localStorage?.setItem("paymentInfoForRedirect", JSON.stringify(paymentInfoForRedirect));
    }

    retryPayment(packageData: PackageDataModel) {
        this.saveInfoInLocalStorageForRedirect({
            redirectUrl: '/',
            userPackage: packageData.userPackage,
            paymentIntent: packageData.paymentIntent
        });
        if (packageData.paymentIntent?.url) {
            this.goToStripe(packageData.paymentIntent.url);
            return;
        }
        this.initiatePaymentIntent(packageData.userPackage.userPackageId)
            .subscribe(response => this.goToStripe(response.url));
    }

    getCreditMovementHistory(
        userId: number,
        params?: CreditMovementHistoryParams
    ): Observable<{ creditMovementHistory: Array<CreditMovementHistoryResponseModel> }> {
        let queryString = '';
        if (params) {
            const queryParams: string[] = [];
            if (params.startDate) queryParams.push(`startDate=${params.startDate}`);
            if (params.endDate) queryParams.push(`endDate=${params.endDate}`);
            if (params.limit !== undefined) queryParams.push(`limit=${params.limit}`);
            if (params.offset !== undefined) queryParams.push(`offset=${params.offset}`);

            if (queryParams.length > 0) {
                queryString = `?${queryParams.join('&')}`;
            }
        }
        return this.apiService.get(`api/booking/credit-movements/${userId}${queryString}`);
    }

    updateAvailableTutorLevels(relations: Array<AppointmentUserInformation>) {
        if (this.currentTab() > (this.showExamPackages() ? 0 : 1)) {
            return;
        }
        this.availableTutorLevels = this.tutorLevels.filter(level => relations.some(relation => +level.level == relation.tutorCategory));
        if (!this.availableTutorLevels.length) {
            this.availableTutorLevels = this.tutorLevels;
        }
        if (this.isDeeplinkFlow()) {
            return;
        }
        if (this.availableTutorLevels.length === 1) {
            this.selectedTutorLevel.set(this.availableTutorLevels[0].level);
        } else if (!this.availableTutorLevels.some(level => level.level === this.selectedTutorLevel())) {
            this.selectedTutorLevel.set(null);
        }
    }

    private addSupremePlusIfNeeded() {
        if (this.utilityService.isNL) {
            const supremePlus = {
                text: 'texts.Packages_tutor_seniority_sp_plus_level',
                level: SeniorityLevelString.supremePlus
            }
            this.tutorLevels.unshift(supremePlus);
        }
    }

    private resetSelectionState() {
        if (!this.isOnPackagePage()) {
            // Delete this when we are usinng examPackages again
            this.showExamPackages.set(false)
        }
        this.showEntryPackages.set(false)
        this.packageCards = []
        this.currentTab.set(0)
        this.availableDurationsMonths = [...DEFAULT_DURATION_OPTIONS_MONTHS];
        this.selectedPackage.set(null)
        this.entryPackage = null
        this.selectedFrequency.set(null)
        this.selectedTutorLevel.set(null)
        this.availableTutorLevels = this.tutorLevels
    }

    private resolvePackageLink(token: string): Observable<PackageLinkModel> {
        const headers = this.utilityService.addHeaders(BypassAPIGlobalHandleEnums.All);
        return this.apiService.getWithHeaders(`api/booking/packages/package-links/${token}`, headers);
    }

    private applyDeeplinkConfig(config: PackageLinkModel) {
        this.deeplinkConfig = config;
        this.showExamPackages.set(false);
        this.showEntryPackages.set(false);
        this.availableDurationsMonths = [...DEFAULT_DURATION_OPTIONS_MONTHS];
        this.packageCards = [];
        this.entryPackage = null;
        this.selectedFrequency.set(String(config.lessonsPerMonth));
        this.selectedTutorLevel.set(String(config.tutorCategory));
        this.selectedPackage.set(null);
        this.isOneInstallment.set(false);
        this.isAfterBooking.set(false);
        this.isWarningVisible.set(false);
        this.hasDismissedPackagesCover.set(true);
    }

    private handleResponse(
        paymentIntent: StripePaymentIntent,
        userPackage: PackageRecommendationModel
    ) {
        this.saveInfoForRedirect(userPackage, paymentIntent);
        this.goToStripe(paymentIntent.url);
    }

    private saveInfoForRedirect(userPackage: PackageRecommendationModel, paymentIntent: StripePaymentIntent) {
        const redirectUrl = this.flowRouteService.initReturnTo(FlowStorageKeys.packagesFlowReturnTo) ?? '/';
        const paymentInfoForRedirect = {
            redirectUrl,
            userPackage: userPackage,
            paymentIntent: paymentIntent,
            packageCards: this.packageCards,
            installmentOption: this.isOneInstallment() ? InstallmentOptionEnum.single : InstallmentOptionEnum.multi,
            paymentMethod: this.isOneInstallment() ? PaymentMethodEnum.single : PaymentMethodEnum.multi,
            bookingDetails: this.bookingDetailsAfterPayment,
            recurrentBookingDetails: this.bookingRecurrentDetailsAfterPayment,
            tutorForename: this.tutorForenameAfterPayment
        } as PaymentInfoForForRedirect
        this.saveInfoInLocalStorageForRedirect(paymentInfoForRedirect);
    }

    private goToStripe(link) {
        location.href = link;
    }
}

